import MalKort from '../components/MalKort';
import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Clock, SubNav, Metric, KpiRow, NorderiKpi, PlaneringTab, FjolRad, shiftISO, YoYUtforskare, VisaFlerRad } from '../components/SharedComponents';
import { tkr, fmtMonth, fmtDate, getISOWeek, raLabel } from '../utils';
import { getTopProducts, getBrasserieSplit, getDailyUnits } from '../qlik';

const RA_LBL = raLabel(); // innevarande räkenskapsår, dynamiskt
const DAG_COLOR      = '#C07A3A';
const KVALL_COLOR    = '#4A1B0C';
const VAV_COLOR      = '#7A6A4F'; // Vävstugan/Butik — egen post i splitraden (ingår i totalen)
// Personal% = lön ÷ intäkt; samma färgtrösklar som övriga portalen
const persPct = (lon, rev) => rev > 0 ? Math.round(Math.abs(lon || 0) / rev * 100) : null;
const persColor = (p) => p == null ? '#ccc' : p <= 35 ? '#3B6D11' : p >= 55 ? '#A32D2D' : '#854F0B';
const VAVSTUGAN_COLOR = '#5B7A4A';
const VAVSTUGAN_BG    = 'rgba(91, 122, 74, 0.07)';
const VAVSTUGAN_BG_H  = 'rgba(91, 122, 74, 0.14)';

function dateStr(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function emptyDay() {
  return { brasserie: 0, brasseriedag: 0, brasseriekvall: 0, vavstugan: 0, timBrass: 0, tb1Brass: 0 };
}

function revPerTim(rev, tim) {
  if (!tim || tim <= 0) return null;
  return Math.round(rev / tim);
}

// Hela kronor i kortens stora belopp — tkr är för grovt för dagsnivå
const krHel = (v) => Math.round(v).toLocaleString('sv-SE') + ' kr';

function TimmarCard({ label, rev, dag, kvall, vav, tim, tb1, lon, fjol, isToday }) {
  const rpt = revPerTim(rev, tim);
  const tg  = rev > 0 ? Math.round(tb1 / rev * 100) : null;
  const pp  = persPct(lon, rev);
  const noData = rev === 0 && tim === 0;

  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 10 }}>
        {label}
      </div>
      {noData ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
          {isToday ? 'Stängt idag' : 'Ingen data'}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', lineHeight: 1.1 }}>
            {krHel(rev)}
          </div>
          <FjolRad cur={rev} fjol={fjol} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginTop: 3 }}>
            {dag > 0 && <span style={{ color: DAG_COLOR }}>Dag {tkr(dag)}</span>}
            {dag > 0 && kvall > 0 && <span style={{ color: 'var(--text-secondary)' }}> · </span>}
            {kvall > 0 && <span style={{ color: KVALL_COLOR }}>Kväll {tkr(kvall)}</span>}
            {(dag > 0 || kvall > 0) && vav > 0 && <span style={{ color: 'var(--text-secondary)' }}> · </span>}
            {vav > 0 && <span style={{ color: VAV_COLOR }}>Butik {tkr(vav)}</span>}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {tim > 0 && (
              <div style={{ fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
                <div style={{ color: 'var(--text-secondary)' }}>Timmar</div>
                <div style={{ fontWeight: 600, color: 'var(--text)' }}>{tim.toFixed(1)} h</div>
              </div>
            )}
            {rpt !== null && (
              <div style={{ fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
                <div style={{ color: 'var(--text-secondary)' }}>Intäkt/timme</div>
                <div style={{ fontWeight: 600, color: 'var(--text)' }}>{rpt.toLocaleString('sv-SE')} kr</div>
              </div>
            )}
            {pp !== null && (
              <div style={{ fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
                <div style={{ color: 'var(--text-secondary)' }}>Personal%</div>
                <div style={{ fontWeight: 600, color: persColor(pp) }}>{pp}%</div>
              </div>
            )}
            {tg !== null && (
              <div style={{ fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
                <div style={{ color: 'var(--text-secondary)' }}>TB1</div>
                <div style={{ fontWeight: 600, color: tb1 >= 0 ? '#3B6D11' : '#A32D2D' }}>{tkr(tb1)} ({tg}%)</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Stacked bar chart (senaste N dagar med data) ──────────────────
function BrasserieStaplar({ daily }) {
  if (!daily || daily.length === 0) return null;

  const days = daily
    .filter(d => (d.brasserie || 0) !== 0 || (d.brasseriedag || 0) !== 0 || (d.brasseriekvall || 0) !== 0)
    .slice(0, 21);

  if (days.length === 0) return (
    <div className="card">
      <div className="card-title">Senaste 21 dagar med data</div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
        Inget data de senaste 90 dagarna — brasserie öppnar 1 juli.
      </p>
    </div>
  );

  const maxVal = Math.max(...days.map(d => d.brasserie || (d.brasseriedag + d.brasseriekvall)), 1);

  return (
    <div className="card">
      <div className="card-title">Senaste {days.length} dagar med data</div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontFamily: 'system-ui,sans-serif', fontSize: 11, color: 'var(--text-secondary)' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: KVALL_COLOR, borderRadius: 2, marginRight: 4 }} />Kväll</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: DAG_COLOR, borderRadius: 2, marginRight: 4 }} />Dag</span>
      </div>
      {days.map((d, i) => {
        const dag   = d.brasseriedag   || 0;
        const kvall = d.brasseriekvall || 0;
        const tot   = d.brasserie || dag + kvall;
        const pDag   = Math.round((dag   / maxVal) * 100);
        const pKvall = Math.round((kvall / maxVal) * 100);
        return (
          <div key={i} className="bar-row">
            <div className="bar-label" style={{ minWidth: 80 }}>{fmtDate(d.datum)}</div>
            <div className="bar-track" style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: pKvall + '%', background: KVALL_COLOR, borderRadius: '4px 0 0 4px' }} />
              <div style={{ position: 'absolute', left: pKvall + '%', top: 0, bottom: 0, width: pDag + '%', background: DAG_COLOR, borderRadius: pKvall === 0 ? '4px' : '0 4px 4px 0' }} />
            </div>
            <div className="bar-val">{tot !== 0 ? tkr(tot) : '–'}</div>
          </div>
        );
      })}
      <div className="note">Källa: Qlik · senaste 90 dagar</div>
    </div>
  );
}

// ── Hjälpfunktioner för veckoaggregering ─────────────────────────
function getMondayStr(datum) {
  const d = new Date(datum);
  const dow = d.getDay() === 0 ? 6 : d.getDay() - 1;
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}

function fmtVecka(mondayStr) {
  const mon = new Date(mondayStr);
  const sun = new Date(mondayStr);
  sun.setDate(mon.getDate() + 6);
  const wn = getISOWeek(mon);
  const f = d => `${d.getDate()}/${d.getMonth() + 1}`;
  return `V${wn} · ${f(mon)}–${f(sun)}`;
}

// ── Gemensam period-tabell (månad eller vecka) ────────────────────
const DAG_BG   = 'rgba(192, 122, 58, 0.07)';
const KVALL_BG = 'rgba(74, 27, 12, 0.05)';
const DAG_BG_H   = 'rgba(192, 122, 58, 0.14)';
const KVALL_BG_H = 'rgba(74, 27, 12, 0.09)';

function BrasserieTabell({ rows, labelFn, title, fjolOf, footer, boldFirst }) {
  // Ifjol-subrad för YoY-coaching — fjolOf(r) → { dag, kvall, vav } | null
  const Ifjol = ({ cur, fjol }) => (fjol || 0) > 0 ? (
    <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 400 }}>
      ifjol {tkr(fjol)}{cur > 0 && <span style={{ fontWeight: 600, color: cur >= fjol ? '#3B6D11' : '#A32D2D' }}> · {cur >= fjol ? '+' : ''}{Math.round((cur - fjol) / fjol * 100)}%</span>}
    </div>
  ) : null;
  return (
    <div className="card">
      <div className="card-title">{title}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui,sans-serif', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 8px 6px 8px', textAlign: 'left', fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Period</th>
              {/* Dag-grupp */}
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: DAG_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, background: DAG_BG_H }}>Dag</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: DAG_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, background: DAG_BG_H, opacity: 0.7 }}>Tim</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: DAG_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, background: DAG_BG_H, opacity: 0.7 }}>Oms/h</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: DAG_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, background: DAG_BG_H, opacity: 0.7 }}>Pers%</th>
              {/* Kväll-grupp */}
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: KVALL_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, background: KVALL_BG_H }}>Kväll</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: KVALL_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, background: KVALL_BG_H, opacity: 0.7 }}>Tim</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: KVALL_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, background: KVALL_BG_H, opacity: 0.7 }}>Oms/h</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: KVALL_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, background: KVALL_BG_H, opacity: 0.7 }}>Pers%</th>
              {/* Vävstugan */}
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: VAVSTUGAN_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, background: VAVSTUGAN_BG_H }}>Vävstugan</th>
              {/* TB1 */}
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>TB1</th>
            </tr>
            <tr><td colSpan={11} style={{ borderBottom: '1px solid var(--border)', padding: 0 }} /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const f = fjolOf ? fjolOf(r) : null;
              return (
              <tr key={i} style={{ borderBottom: boldFirst && i === 0 ? '2px solid var(--border)' : '1px solid #f5f5f0', fontWeight: boldFirst && i === 0 ? 700 : 400 }}>
                <td style={{ padding: '9px 8px', color: 'var(--text)' }}>{labelFn(r)}</td>
                {/* Dag */}
                <td style={{ padding: '9px 8px', textAlign: 'right', color: DAG_COLOR, background: DAG_BG }}>
                  {r.brasseriedag > 0 ? tkr(r.brasseriedag) : '–'}
                  <Ifjol cur={r.brasseriedag} fjol={f?.dag} />
                </td>
                <td style={{ padding: '9px 8px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: 12, background: DAG_BG }}>{r.timBrassDag > 0 ? r.timBrassDag.toFixed(0) + ' h' : '–'}</td>
                <td style={{ padding: '9px 8px', textAlign: 'right', color: DAG_COLOR, fontSize: 12, fontWeight: 500, background: DAG_BG }}>{r.timBrassDag > 0 && r.brasseriedag > 0 ? Math.round(r.brasseriedag / r.timBrassDag).toLocaleString('sv-SE') + ' kr' : '–'}</td>
                <td style={{ padding: '9px 8px', textAlign: 'right', color: DAG_COLOR, fontSize: 12, fontWeight: 500, background: DAG_BG }}>{r.brasseriedag > 0 && (r.lonBrassDag || 0) !== 0 ? Math.round(Math.abs(r.lonBrassDag) / r.brasseriedag * 100) + '%' : '–'}</td>
                {/* Kväll */}
                <td style={{ padding: '9px 8px', textAlign: 'right', color: KVALL_COLOR, background: KVALL_BG }}>
                  {r.brasseriekvall > 0 ? tkr(r.brasseriekvall) : '–'}
                  <Ifjol cur={r.brasseriekvall} fjol={f?.kvall} />
                </td>
                <td style={{ padding: '9px 8px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: 12, background: KVALL_BG }}>{r.timBrassKvall > 0 ? r.timBrassKvall.toFixed(0) + ' h' : '–'}</td>
                <td style={{ padding: '9px 8px', textAlign: 'right', color: KVALL_COLOR, fontSize: 12, fontWeight: 500, background: KVALL_BG }}>{r.timBrassKvall > 0 && r.brasseriekvall > 0 ? Math.round(r.brasseriekvall / r.timBrassKvall).toLocaleString('sv-SE') + ' kr' : '–'}</td>
                <td style={{ padding: '9px 8px', textAlign: 'right', color: KVALL_COLOR, fontSize: 12, fontWeight: 500, background: KVALL_BG }}>{r.brasseriekvall > 0 && (r.lonBrassKvall || 0) !== 0 ? Math.round(Math.abs(r.lonBrassKvall) / r.brasseriekvall * 100) + '%' : '–'}</td>
                {/* Vävstugan */}
                <td style={{ padding: '9px 8px', textAlign: 'right', color: VAVSTUGAN_COLOR, fontWeight: 500, background: VAVSTUGAN_BG }}>
                  {(r.vavstugan || 0) > 0 ? tkr(r.vavstugan) : '–'}
                  <Ifjol cur={r.vavstugan || 0} fjol={f?.vav} />
                </td>
                {/* TB1 */}
                <td style={{ padding: '9px 8px', textAlign: 'right', color: r.tb1Brass >= 0 ? '#3B6D11' : '#A32D2D', fontWeight: 500 }}>{r.tb1Brass !== 0 ? tkr(r.tb1Brass) : '–'}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
      <div className="note">Källa: Qlik — Brasserie Kerstin + Vävstugan · Personalkollen</div>
    </div>
  );
}

// ── Populäraste produkter ─────────────────────────────────────────
const PERIOD_LABELS = [
  { key: 'today',       label: 'Idag'      },
  { key: 'yesterday',   label: 'Igår'      },
  { key: 'week',        label: 'Vecka'     },
  { key: 'month',       label: 'Månad'     },
  { key: 'threemonths', label: '3 månader' },
];

function ProductColumn({ items, sortBy, maxVal, color, label }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>{label}</div>
      {items.length === 0
        ? <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen data</div>
        : items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <div style={{ width: 12, fontSize: 10, color: '#bbb', textAlign: 'right', fontFamily: 'system-ui,sans-serif', flexShrink: 0 }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 2 }}>{item.name}</div>
              <div style={{ height: 3, background: 'var(--panel2)', borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${Math.round((item[sortBy] / maxVal) * 100)}%`, background: color, borderRadius: 2 }} />
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', flexShrink: 0, textAlign: 'right', minWidth: 52 }}>
              {sortBy === 'antal' ? `${item.antal} st` : `${Math.round(item.intakt).toLocaleString('sv-SE')} kr`}
            </div>
          </div>
        ))
      }
    </div>
  );
}

function TopProduktCard() {
  const [period,   setPeriod]   = useState('yesterday');
  const [sortBy,   setSortBy]   = useState('antal');
  const [products, setProducts] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [visaAlla, setVisaAlla] = useState(false);

  useEffect(() => {
    getTopProducts()
      .then(p => setProducts(p))
      .catch(e => console.error('TopProducts error:', e))
      .finally(() => setLoading(false));
  }, []);

  const periodData = products?.[period] || { dag: [], kvall: [] };
  const dagFull    = [...(periodData.dag   || [])].sort((a, b) => b[sortBy] - a[sortBy]);
  const kvallFull  = [...(periodData.kvall || [])].sort((a, b) => b[sortBy] - a[sortBy]);
  const dagItems   = visaAlla ? dagFull   : dagFull.slice(0, 7);
  const kvallItems = visaAlla ? kvallFull : kvallFull.slice(0, 7);
  const allMax     = Math.max(...[...dagItems, ...kvallItems].map(i => i[sortBy] || 0), 1);

  const sortBtn = (key, label) => (
    <button key={key} onClick={() => setSortBy(key)} style={{
      background: sortBy === key ? '#888' : 'transparent',
      color: sortBy === key ? '#fff' : 'var(--text-secondary)',
      border: '1px solid var(--border)', borderRadius: 20,
      padding: '2px 8px', fontSize: 10, cursor: 'pointer',
      fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
    }}>{label}</button>
  );

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Populäraste produkter</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {PERIOD_LABELS.map(({ key, label }) => (
            <button key={key} onClick={() => setPeriod(key)} style={{
              background: period === key ? KVALL_COLOR : 'transparent',
              color: period === key ? '#fff' : 'var(--text-secondary)',
              border: '1px solid var(--border)', borderRadius: 20,
              padding: '3px 10px', fontSize: 11, cursor: 'pointer',
              fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {sortBtn('antal', 'Antal sålda')}
        {sortBtn('intakt', 'Intäkt')}
      </div>
      {loading && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar produktdata...</div>}
      {!loading && (
        <>
          <div style={{ display: 'flex', gap: 12 }}>
            <ProductColumn items={dagItems}   sortBy={sortBy} maxVal={allMax} color={DAG_COLOR}   label="Dag" />
            <div style={{ width: 1, background: 'var(--border)', flexShrink: 0 }} />
            <ProductColumn items={kvallItems} sortBy={sortBy} maxVal={allMax} color={KVALL_COLOR} label="Kväll" />
          </div>
          <VisaFlerRad total={Math.max(dagFull.length, kvallFull.length)} expanded={visaAlla} onToggle={() => setVisaAlla(v => !v)} />
        </>
      )}
      <div className="note" style={{ marginTop: 10 }}>Källa: Qlik · sålda artiklar per period</div>
    </div>
  );
}

// ── FÖRSÄLJNING PER HUVUDGRUPP ────────────────────────────────────
// Kassans X-rapportgrupper (Förrätt/Varmrätt/Dessert/dryckerna) återskapade
// i portalen. Visbooks varugrupper finns INTE i Qlik-laddningen, så
// mappningen produkt → grupp bor i intel_config/brasserie_grupper och
// underhålls här (admin): nya produkter hamnar i Okategoriserat tills
// de tilldelats. Datat = samma produktcache som Populäraste produkter.
const HUVUDGRUPPER = ['Förrätt', 'Varmrätt', 'Lunch', 'Dessert', 'Fikabröd', 'Öl', 'Rött vin', 'Vitt vin', 'Cider', 'Sprit', 'Varm dryck', 'Alkoholfri dryck', 'Övrigt'];

function HuvudgruppCard({ isAdmin }) {
  const [period, setPeriod] = useState('yesterday');
  const [products, setProducts] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [redigera, setRedigera] = useState(false);
  const [dirtyMap, setDirtyMap] = useState(false);
  const [sparar, setSparar] = useState(false);

  useEffect(() => {
    getTopProducts().then(setProducts).catch(e => console.error('Huvudgrupper products:', e));
    getDoc(doc(db, 'intel_config', 'brasserie_grupper'))
      .then(s => setMapping(s.exists() ? (s.data().mapping || {}) : {}))
      .catch(e => { console.error('Huvudgrupper mapping:', e); setMapping({}); });
  }, []);

  const periodData = products?.[period] || { dag: [], kvall: [] };
  const fold = (items) => {
    const by = {};
    for (const it of items) {
      const g = (mapping || {})[it.name] || 'Okategoriserat';
      if (!by[g]) by[g] = { grupp: g, antal: 0, intakt: 0 };
      by[g].antal += it.antal;
      by[g].intakt += it.intakt;
    }
    return by;
  };
  const dagG = fold(periodData.dag || []);
  const kvallG = fold(periodData.kvall || []);
  const grupper = [...HUVUDGRUPPER.filter(g => dagG[g] || kvallG[g]), ...(dagG['Okategoriserat'] || kvallG['Okategoriserat'] ? ['Okategoriserat'] : [])];
  const totAntal = (g) => (dagG[g]?.antal || 0) + (kvallG[g]?.antal || 0);
  const sumCell = (o) => o ? `${o.antal} st · ${Math.round(o.intakt).toLocaleString('sv-SE')} kr` : '–';

  // Redigeringsläget: alla produkter (3 mån, dag+kväll) med gruppval
  const allaProdukter = useMemo(() => {
    if (!products) return [];
    const tm = products.threemonths || { dag: [], kvall: [] };
    const set = new Map();
    for (const it of [...(tm.dag || []), ...(tm.kvall || [])]) {
      const cur = set.get(it.name) || { name: it.name, antal: 0 };
      cur.antal += it.antal;
      set.set(it.name, cur);
    }
    return [...set.values()].sort((a, b) => b.antal - a.antal);
  }, [products]);

  const sparaMapping = async () => {
    setSparar(true);
    try {
      await setDoc(doc(db, 'intel_config', 'brasserie_grupper'), {
        mapping, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || '?',
      });
      setDirtyMap(false);
    } catch (e) { console.error('spara mapping:', e); }
    finally { setSparar(false); }
  };

  const okatAntal = allaProdukter.filter(p => !(mapping || {})[p.name]).length;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Försäljning per huvudgrupp</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {PERIOD_LABELS.map(({ key, label }) => (
            <button key={key} onClick={() => setPeriod(key)} style={{
              background: period === key ? KVALL_COLOR : 'transparent',
              color: period === key ? '#fff' : 'var(--text-secondary)',
              border: '1px solid var(--border)', borderRadius: 20,
              padding: '3px 10px', fontSize: 11, cursor: 'pointer',
              fontFamily: 'system-ui,sans-serif',
            }}>{label}</button>
          ))}
          {/* Alla som ser Brasserie-blocket får underhålla grupperna
              (behörigheten till blocket ÄR behörigheten; rules speglar det) */}
          <button onClick={() => setRedigera(r => !r)} style={{
            background: redigera ? '#854F0B' : 'transparent', color: redigera ? '#fff' : 'var(--text-secondary)',
            border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px', fontSize: 11,
            cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
          }}>✎ Grupper{okatAntal > 0 ? ` (${okatAntal})` : ''}</button>
        </div>
      </div>

      {(!products || mapping === null) && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar…</div>}

      {products && mapping !== null && !redigera && (
        <div className="period-table">
          <div className="period-header period-row-4">
            <span>Huvudgrupp</span>
            <span style={{ textAlign: 'right' }}>Dag</span>
            <span style={{ textAlign: 'right' }}>Kväll</span>
            <span style={{ textAlign: 'right' }}>Totalt</span>
          </div>
          {grupper.map((g) => (
            <div key={g} className="period-row period-row-4" style={g === 'Okategoriserat' ? { color: '#854F0B' } : undefined}>
              <span style={{ fontWeight: 600 }}>{g}</span>
              <span style={{ textAlign: 'right' }}>{sumCell(dagG[g])}</span>
              <span style={{ textAlign: 'right' }}>{sumCell(kvallG[g])}</span>
              <span style={{ textAlign: 'right', fontWeight: 700 }}>
                {totAntal(g)} st · {Math.round((dagG[g]?.intakt || 0) + (kvallG[g]?.intakt || 0)).toLocaleString('sv-SE')} kr
              </span>
            </div>
          ))}
          {grupper.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 0', fontFamily: 'system-ui,sans-serif' }}>Ingen försäljning i perioden.</div>}
          {(() => {
            const ta = grupper.reduce((s, g) => s + totAntal(g), 0);
            const ti = grupper.reduce((s, g) => s + (dagG[g]?.intakt || 0) + (kvallG[g]?.intakt || 0), 0);
            return ta > 0 ? (
              <div className="period-row period-row-4" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <span>Summa</span>
                <span style={{ textAlign: 'right' }}>{sumCell({ antal: (periodData.dag || []).reduce((s, i) => s + i.antal, 0), intakt: (periodData.dag || []).reduce((s, i) => s + i.intakt, 0) })}</span>
                <span style={{ textAlign: 'right' }}>{sumCell({ antal: (periodData.kvall || []).reduce((s, i) => s + i.antal, 0), intakt: (periodData.kvall || []).reduce((s, i) => s + i.intakt, 0) })}</span>
                <span style={{ textAlign: 'right' }}>{ta} st · {Math.round(ti).toLocaleString('sv-SE')} kr</span>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {products && mapping !== null && redigera && (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>
            Tilldela huvudgrupp per produkt (sorterat på sålda senaste 3 mån). Osatta produkter räknas som Okategoriserat.
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px' }}>
            {allaProdukter.map((p) => (
              <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--panel2)', fontFamily: 'system-ui,sans-serif' }}>
                <span style={{ fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name} <span style={{ color: 'var(--text-secondary)', fontSize: 10.5 }}>({p.antal} st)</span>
                </span>
                <select value={(mapping || {})[p.name] || ''} onChange={(e) => {
                  setMapping((m) => {
                    const ny = { ...m };
                    if (e.target.value) ny[p.name] = e.target.value; else delete ny[p.name];
                    return ny;
                  });
                  setDirtyMap(true);
                }} style={{ fontSize: 11.5, padding: '2px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', flexShrink: 0 }}>
                  <option value="">— Okategoriserat</option>
                  {HUVUDGRUPPER.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
            <button onClick={sparaMapping} disabled={!dirtyMap || sparar} style={{
              background: dirtyMap ? '#854F0B' : 'transparent', color: dirtyMap ? '#fff' : 'var(--text-secondary)',
              border: '1px solid var(--border)', borderRadius: 8, padding: '5px 14px', fontSize: 12,
              cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
            }}>{sparar ? 'Sparar…' : 'Spara grupper'}</button>
          </div>
        </>
      )}

      <div className="note" style={{ marginTop: 10 }}>Samma grupper som kassans X-rapport. Belopp ex. moms (kassan visar ink. moms). Antal med kvantitetsuppskattning per rad.</div>
    </div>
  );
}

// ── MAIN BLOCK ────────────────────────────────────────────────────
export default function BrasserieBlock({ data, isAdmin = false }) {
  const [sub, setSub]             = useState('daglig');
  const [periodeFilter, setPeriodeFilter] = useState('dagar');
  const [visaAllaManader, setVisaAllaManader] = useState(false);
  // Fri period (Period-fliken) — default senaste 30 dagarna
  const [pFrom, setPFrom] = useState(() => shiftISO(new Date().toLocaleDateString('sv-SE'), -29));
  const [pTo, setPTo]     = useState(() => new Date().toLocaleDateString('sv-SE'));
  // Fjolårssiffror ur daily_units-cachen (full historik) — daily-cachen har bara 90 dgr
  const [units, setUnits] = useState(null);
  useEffect(() => { getDailyUnits().then(setUnits).catch(() => setUnits([])); }, []);
  // Dag/kväll-split med full historik (för ifjol i dags-/veckotabellen)
  const [brassSplit, setBrassSplit] = useState(null);
  useEffect(() => { getBrasserieSplit().then(setBrassSplit).catch(() => setBrassSplit({})); }, []);

  const monthlyRows = (data?.monthly || []).map(r => ({
    arManad:        r[0],
    brasserie:      r[2],
    timBrass:       r[18] || 0,
    brasseriedag:   r[26] || 0,
    brasseriekvall: r[27] || 0,
    tb1Brass:       r[28] || 0,
    tb1BrassDag:    r[29] || 0,
    tb1BrassKvall:  r[30] || 0,
    timBrassDag:    r[31] || 0,
    timBrassKvall:  r[32] || 0,
    vavstugan:      r[36] || 0,
    lonBrassDag:    r[49] || 0,
    lonBrassKvall:  r[50] || 0,
  }));

  // Hämta från ytd om tillgängligt
  const ytd = data?.ytd || {};
  const ytdTot        = ytd.brasserieKerstin || 0;
  const ytdDag        = ytd.brasseriedag     || 0;
  const ytdKvall      = ytd.brasseriekvall   || 0;
  const ytdVavstugan  = ytd.vavstugan        || 0;
  const ytdTB1        = ytd.tb1Brasserie     || 0;
  const ytdTB1Dag     = ytd.tb1BrassDag      || 0;
  const ytdTB1Kvall   = ytd.tb1BrassKvall    || 0;
  const ytdTB1Vavstugan = ytd.tb1Vavstugan   || 0;
  const daily = data?.daily || [];

  // Veckoaggregering från daily-data
  const weekRows = (() => {
    const map = {};
    daily.forEach(d => {
      const mon = getMondayStr(d.datum);
      if (!map[mon]) map[mon] = { label: mon, brasserie: 0, brasseriedag: 0, brasseriekvall: 0, vavstugan: 0, timBrass: 0, timBrassDag: 0, timBrassKvall: 0, tb1Brass: 0, lonBrassDag: 0, lonBrassKvall: 0 };
      map[mon].brasserie      += d.brasserie      || 0;
      map[mon].brasseriedag   += d.brasseriedag   || 0;
      map[mon].brasseriekvall += d.brasseriekvall || 0;
      map[mon].vavstugan      += d.vavstugan      || 0;
      map[mon].timBrass       += d.timBrass       || 0;
      map[mon].timBrassDag    += d.timBrassDag    || 0;
      map[mon].timBrassKvall  += d.timBrassKvall  || 0;
      map[mon].tb1Brass       += d.tb1Brass       || 0;
      map[mon].lonBrassDag    += d.lonBrassDag    || 0;
      map[mon].lonBrassKvall  += d.lonBrassKvall  || 0;
    });
    return Object.values(map)
      .filter(w => w.brasserie > 0 || w.timBrass > 0)
      .sort((a, b) => b.label.localeCompare(a.label));
  })();

  // Idag / igår / förra veckan samma dag
  const todayS    = dateStr(0);
  const ydayS     = dateStr(-1);
  const lw7S      = dateStr(-7);
  const rowToday  = daily.find(d => d.datum === todayS)  || emptyDay();
  const rowYday   = daily.find(d => d.datum === ydayS)   || emptyDay();
  const rowLW7    = daily.find(d => d.datum === lw7S)    || emptyDay();

  // Fjolår (samma veckodag, −364 dgr) för Idag/Igår/Senaste 7 — inkl. Vävstugan/Butik
  const brSum = (from, to) => (units || []).reduce((s, u) =>
    (u.datum >= from && u.datum <= to && (u.enhet === 'Brasserie Kerstin' || u.enhet === 'Vävstugan')) ? s + (u.intakt || 0) : s, 0);
  const fjolIdag = brSum(shiftISO(todayS, -364), shiftISO(todayS, -364));
  const fjolYday = brSum(shiftISO(ydayS, -364), shiftISO(ydayS, -364));
  const fjolW7   = brSum(shiftISO(lw7S, -364), shiftISO(todayS, -364));

  // fjolOf för period-tabellerna: dag/kväll ur brass_split, Vävstugan ur daily_units
  const vavSum = (from, to) => (units || []).reduce((s, u) =>
    (u.datum >= from && u.datum <= to && u.enhet === 'Vävstugan') ? s + (u.intakt || 0) : s, 0);
  const fjolOfDag = (r) => {
    const iso = shiftISO(r.datum, -364);
    const s = (brassSplit || {})[iso];
    const vav = vavSum(iso, iso);
    if (!s && !vav) return null;
    return { dag: s?.oms?.[0] || 0, kvall: s?.oms?.[1] || 0, vav };
  };
  const fjolOfVecka = (r) => {
    let dag = 0, kvall = 0, any = false;
    for (let i = 0; i < 7; i++) {
      const s = (brassSplit || {})[shiftISO(r.label, i - 364)];
      if (s) { dag += s.oms?.[0] || 0; kvall += s.oms?.[1] || 0; any = true; }
    }
    const vav = vavSum(shiftISO(r.label, -364), shiftISO(r.label, -358));
    return (any || vav) ? { dag, kvall, vav } : null;
  };
  // ── FRI PERIOD ────────────────────────────────────────────────
  // Bygger rader ur full historik: dag/kväll (oms, lön, timmar) ur
  // brass_split, Vävstugan + TB1 ur daily_units. Ger samma kolumner som
  // övriga vyer men för valfritt datumintervall — och för jämförelse ifjol.
  const periodAgg = (from, to) => {
    const a = { brasseriedag: 0, brasseriekvall: 0, vavstugan: 0, timBrassDag: 0, timBrassKvall: 0,
                lonBrassDag: 0, lonBrassKvall: 0, tb1Brass: 0 };
    for (const [datum, s] of Object.entries(brassSplit || {})) {
      if (datum < from || datum > to) continue;
      a.brasseriedag   += s.oms?.[0] || 0;
      a.brasseriekvall += s.oms?.[1] || 0;
      a.lonBrassDag    += s.pers?.[0] || 0;
      a.lonBrassKvall  += s.pers?.[1] || 0;
      a.timBrassDag    += s.tim?.[0] || 0;
      a.timBrassKvall  += s.tim?.[1] || 0;
    }
    for (const u of (units || [])) {
      if (u.datum < from || u.datum > to) continue;
      if (u.enhet === 'Vävstugan') a.vavstugan += u.intakt || 0;
      if (u.enhet === 'Vävstugan' || u.enhet === 'Brasserie Kerstin') a.tb1Brass += (u.intakt || 0) + (u.kostnad || 0);
    }
    return a;
  };
  // Uppdelning inuti perioden: dag → vecka → månad beroende på längd
  const periodRader = (() => {
    if (!pFrom || !pTo || pFrom > pTo || !brassSplit || !units) return [];
    const dagar = Math.round((new Date(pTo) - new Date(pFrom)) / 86400000) + 1;
    const ut = [];
    if (dagar <= 31) {
      for (let d = pTo; d >= pFrom; d = shiftISO(d, -1)) ut.push({ label: d, from: d, to: d, typ: 'dag' });
    } else if (dagar <= 190) {
      let mon = getMondayStr(pTo);
      while (mon >= pFrom) {
        const slut = shiftISO(mon, 6);
        ut.push({ label: mon, from: mon < pFrom ? pFrom : mon, to: slut > pTo ? pTo : slut, typ: 'vecka' });
        mon = shiftISO(mon, -7);
      }
    } else {
      let ym = pTo.slice(0, 7);
      while (ym >= pFrom.slice(0, 7)) {
        const fd = `${ym}-01`;
        const [y, m] = ym.split('-').map(Number);
        const ld = `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
        ut.push({ label: ym, from: fd < pFrom ? pFrom : fd, to: ld > pTo ? pTo : ld, typ: 'manad' });
        const pm = new Date(y, m - 2, 1);
        ym = `${pm.getFullYear()}-${String(pm.getMonth() + 1).padStart(2, '0')}`;
      }
    }
    return ut.map(b => ({ ...b, ...periodAgg(b.from, b.to) }))
             .filter(r => r.brasseriedag > 0 || r.brasseriekvall > 0 || r.vavstugan > 0 || r.timBrassDag > 0 || r.timBrassKvall > 0);
  })();
  const periodTot = { label: 'HELA PERIODEN', from: pFrom, to: pTo, typ: 'total', ...periodAgg(pFrom, pTo) };
  const fjolOfPeriod = (r) => {
    const f = periodAgg(shiftISO(r.from, -364), shiftISO(r.to, -364));
    return (f.brasseriedag || f.brasseriekvall || f.vavstugan)
      ? { dag: f.brasseriedag, kvall: f.brasseriekvall, vav: f.vavstugan } : null;
  };
  const periodLabel = (r) => {
    if (r.typ === 'total') return 'Hela perioden';
    if (r.typ === 'dag')   return fmtDate(r.label);
    if (r.typ === 'vecka') return fmtVecka(r.label);
    const [y, m] = r.label.split('-').map(Number);
    return fmtMonth(`${y}${String(m).padStart(2, '0')}`);
  };

  const fjolOfManad = (r) => {
    const p = monthlyRows.find(x => Number(x.arManad) === Number(r.arManad) - 100);
    return p ? { dag: p.brasseriedag, kvall: p.brasseriekvall, vav: p.vavstugan } : null;
  };

  // Senaste 7 dagars summor
  const last7 = daily.filter(d => d.datum >= lw7S && d.datum <= todayS);
  const w7Tot   = last7.reduce((s, d) => s + (d.brasserie      || 0), 0);
  const w7Dag   = last7.reduce((s, d) => s + (d.brasseriedag   || 0), 0);
  const w7Kvall = last7.reduce((s, d) => s + (d.brasseriekvall || 0), 0);
  const w7Vav   = last7.reduce((s, d) => s + (d.vavstugan      || 0), 0);
  const w7Lon   = last7.reduce((s, d) => s + (d.lonBrassDag || 0) + (d.lonBrassKvall || 0), 0);
  const w7Tim   = last7.reduce((s, d) => s + (d.timBrass       || 0), 0);
  const w7TB1   = last7.reduce((s, d) => s + (d.tb1Brass       || 0), 0);

  // Timmar-analys YTD (from monthly ytd)
  const ytdTimBrass = ytd.timBrass || 0;
  const ytdRevPerTim = revPerTim(ytdTot, ytdTimBrass);
  const ytdPersBrass = ytdTot > 0 ? Math.round(Math.abs(ytd.lonBrasserie || 0) / ytdTot * 100) : 0;

  // Nörderi-data från ytd
  const ytdKvittonKvall  = ytd.kvittonBrassKvall  || 0;
  const ytdKvittonDag    = ytd.kvittonBrassDag    || 0;
  const ytdKvittonTot    = ytd.kvittonBrass       || 0;
  const ytdPortKvall     = ytd.portionerBrassKvall || 0;
  const ytdPortDag       = ytd.portionerBrassDag  || 0;
  const ytdPortTot       = ytd.portionerBrass     || 0;
  const ytdTimBrassDag   = ytd.timBrassDag        || 0;
  const ytdTimBrassKvall = ytd.timBrassKvall       || 0;

  const sn = (a, b) => b > 0 ? Math.round(a / b) : null;
  const snTkr = (a, b) => b > 0 ? Math.round(a / b / 1000) : null;

  // Bästa & sämsta månad på oms/h
  const mRows = monthlyRows.filter(r => r.timBrass > 0 && r.brasserie > 0);
  const bestMaand  = mRows.length ? mRows.reduce((a,b) => (b.brasserie/b.timBrass) > (a.brasserie/a.timBrass) ? b : a, mRows[0]) : null;
  const worstMaand = mRows.length ? mRows.reduce((a,b) => (b.brasserie/b.timBrass) < (a.brasserie/a.timBrass) ? b : a, mRows[0]) : null;

  const TABS = [
    { id: 'daglig', label: 'Daglig översikt' },
    { id: 'period', label: 'Period & trend'  },
    { id: 'planering', label: 'För planering' },
    { id: 'norderi', label: 'Övrigt nörderi' },
  ];

  return (
    <div className="screen">
      <div className="header header-brasserie">
        <div>
          <div className="header-title">Brasserie Kerstin + Vävstugan</div>
          <div className="header-name">Försäljning & lönsamhet</div>
        </div>
        <Clock />
      </div>
      <SubNav tabs={TABS} active={sub} onChange={setSub} />
      {sub === 'daglig' && <MalKort enheter={['Brasserie Kerstin','Vävstugan']} />}

      {/* ── DAGLIG ÖVERSIKT ── */}
      {sub === 'daglig' && (
        <>
          {/* Idag / Igår */}
          <div className="grid-2">
            <div className="card">
              <TimmarCard
                label="Idag"
                rev={rowToday.brasserie}
                fjol={fjolIdag}
                dag={rowToday.brasseriedag}
                kvall={rowToday.brasseriekvall}
                vav={rowToday.vavstugan}
                tim={rowToday.timBrass}
                tb1={rowToday.tb1Brass}
                lon={(rowToday.lonBrassDag || 0) + (rowToday.lonBrassKvall || 0)}
                isToday
              />
            </div>
            <div className="card">
              <TimmarCard
                label="Igår"
                rev={rowYday.brasserie}
                fjol={fjolYday}
                dag={rowYday.brasseriedag}
                kvall={rowYday.brasseriekvall}
                vav={rowYday.vavstugan}
                tim={rowYday.timBrass}
                tb1={rowYday.tb1Brass}
                lon={(rowYday.lonBrassDag || 0) + (rowYday.lonBrassKvall || 0)}
              />
            </div>
          </div>

          {/* Senaste 7 dagar + Förra veckan samma dag */}
          <div className="grid-2">
            <div className="card">
              <div className="card-title">Senaste 7 dagarna</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', fontFamily: 'system-ui,sans-serif' }}>
                {w7Tot > 0 ? krHel(w7Tot) : '–'}
              </div>
              <FjolRad cur={w7Tot} fjol={fjolW7} />
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginTop: 3 }}>
                {w7Dag > 0 && <span style={{ color: DAG_COLOR }}>Dag {tkr(w7Dag)}</span>}
                {w7Dag > 0 && w7Kvall > 0 && ' · '}
                {w7Kvall > 0 && <span style={{ color: KVALL_COLOR }}>Kväll {tkr(w7Kvall)}</span>}
                {(w7Dag > 0 || w7Kvall > 0) && w7Vav > 0 && ' · '}
                {w7Vav > 0 && <span style={{ color: VAV_COLOR }}>Butik {tkr(w7Vav)}</span>}
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
                {w7Tim > 0 && (
                  <div>
                    <div style={{ color: 'var(--text-secondary)' }}>Timmar</div>
                    <div style={{ fontWeight: 600 }}>{w7Tim.toFixed(1)} h</div>
                  </div>
                )}
                {revPerTim(w7Tot, w7Tim) !== null && (
                  <div>
                    <div style={{ color: 'var(--text-secondary)' }}>Intäkt/timme</div>
                    <div style={{ fontWeight: 600 }}>{revPerTim(w7Tot, w7Tim).toLocaleString('sv-SE')} kr</div>
                  </div>
                )}
                {persPct(w7Lon, w7Tot) !== null && (
                  <div>
                    <div style={{ color: 'var(--text-secondary)' }}>Personal%</div>
                    <div style={{ fontWeight: 600, color: persColor(persPct(w7Lon, w7Tot)) }}>{persPct(w7Lon, w7Tot)}%</div>
                  </div>
                )}
                {w7Tot > 0 && (
                  <div>
                    <div style={{ color: 'var(--text-secondary)' }}>TB1</div>
                    <div style={{ fontWeight: 600, color: w7TB1 >= 0 ? '#3B6D11' : '#A32D2D' }}>{tkr(w7TB1)}</div>
                  </div>
                )}
              </div>
              <div className="note" style={{ marginTop: 12 }}>{lw7S} – {todayS}</div>
            </div>
            <div className="card">
              <div className="card-title">Förra veckan samma dag</div>
              <TimmarCard
                label={fmtDate(lw7S)}
                rev={rowLW7.brasserie}
                dag={rowLW7.brasseriedag}
                kvall={rowLW7.brasseriekvall}
                vav={rowLW7.vavstugan}
                tim={rowLW7.timBrass}
                tb1={rowLW7.tb1Brass}
                lon={(rowLW7.lonBrassDag || 0) + (rowLW7.lonBrassKvall || 0)}
              />
            </div>
          </div>

          {/* Timmar-analys */}
          <div className="card">
            <div className="card-title">Timmar & produktivitet — {RA_LBL}</div>
            <div className="grid-4">
              <div style={{ fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Intäkt/timme YTD</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
                  {ytdRevPerTim !== null ? ytdRevPerTim.toLocaleString('sv-SE') + ' kr' : '–'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                  {ytdTimBrass > 0 ? `${ytdTimBrass.toFixed(0)} h totalt` : 'Inga timmar YTD'}
                </div>
              </div>
              <div style={{ fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Personal% YTD</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: persColor(ytdTot > 0 ? ytdPersBrass : null) }}>
                  {ytdTot > 0 ? `${ytdPersBrass}%` : '–'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                  {ytdTot > 0 ? `Lön ${tkr(Math.abs(ytd.lonBrasserie || 0))}` : 'Lön ÷ intäkt'}
                </div>
              </div>
              <div style={{ fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>TB1 kväll YTD</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: ytdTB1Kvall >= 0 ? '#3B6D11' : '#A32D2D' }}>
                  {ytdKvall > 0 ? Math.round(ytdTB1Kvall / ytdKvall * 100) + '%' : '–'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                  {ytdTB1Kvall > 0 ? `TB1 ${tkr(ytdTB1Kvall)}` : 'À la carte motor'}
                </div>
              </div>
              <div style={{ fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>TB1 dag YTD</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: ytdTB1Dag >= 0 ? '#3B6D11' : '#A32D2D' }}>
                  {ytdDag > 0 ? Math.round(ytdTB1Dag / ytdDag * 100) + '%' : '–'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                  {ytdTB1Dag !== 0 ? `TB1 ${tkr(ytdTB1Dag)}` : 'Lunch + fika'}
                </div>
              </div>
            </div>
            <div className="note" style={{ marginTop: 12 }}>
              Timmar från Personalkollen · TB1 = Intäkt + Varukostnad + Lönekostnad
            </div>
          </div>

          <TopProduktCard />

          <HuvudgruppCard isAdmin={isAdmin} />

          <BrasserieStaplar daily={daily} />
        </>
      )}

      {/* ── PERIOD & TREND ── */}
      {sub === 'period' && (
        <>
          <div className="grid-4">
            <Metric
              label="Brasserie kväll YTD"
              value={ytdKvall > 0 ? tkr(ytdKvall) : '–'}
              sub={ytdTB1Kvall !== 0 ? `TB1 ${tkr(ytdTB1Kvall)} · TG ${Math.round(ytdTB1Kvall / Math.max(ytdKvall,1) * 100)}%` : 'À la carte'}
              trend={ytdTB1Kvall > 0 ? 'up' : ''}
            />
            <Metric
              label="Brasserie dag YTD"
              value={ytdDag > 0 ? tkr(ytdDag) : '–'}
              sub={ytdTB1Dag !== 0 ? `TB1 ${tkr(ytdTB1Dag)} · TG ${Math.round(ytdTB1Dag / Math.max(ytdDag,1) * 100)}%` : 'Lunch + fika'}
            />
            <Metric
              label="Vävstugan YTD"
              value={ytdVavstugan > 0 ? tkr(ytdVavstugan) : '–'}
              sub={ytdTB1Vavstugan !== 0 ? `TB1 ${tkr(ytdTB1Vavstugan)} · TG ${Math.round(ytdTB1Vavstugan / Math.max(ytdVavstugan,1) * 100)}%` : 'Hantverksprodukter'}
              trend={ytdTB1Vavstugan > 0 ? 'up' : ''}
            />
            <Metric
              label="Totalt YTD"
              value={ytdTot > 0 ? tkr(ytdTot) : '–'}
              sub={ytdTB1 !== 0 ? `TB1 ${tkr(ytdTB1)}` : 'Brasserie + Vävstugan'}
              trend={ytdTB1 > 0 ? 'up' : ''}
            />
          </div>

          {/* Dagar / Vecka / Månad-filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 2, fontFamily: 'system-ui,sans-serif' }}>
            {[{ id: 'dagar', label: 'Dagar' }, { id: 'vecka', label: 'Vecka' }, { id: 'manad', label: 'Månad' }, { id: 'period', label: 'Period' }].map(f => (
              <button
                key={f.id}
                onClick={() => setPeriodeFilter(f.id)}
                style={{
                  padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 500, transition: 'all 0.15s',
                  background: periodeFilter === f.id ? '#1B2A4A' : '#f0f0ea',
                  color:      periodeFilter === f.id ? '#fff'     : 'var(--text-secondary)',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {periodeFilter === 'dagar' && (
            <BrasserieTabell
              rows={daily.slice(0, 14)}
              labelFn={r => fmtDate(r.datum)}
              title="Daglig översikt — senaste 14 dagarna"
              fjolOf={fjolOfDag}
            />
          )}

          {periodeFilter === 'vecka' && (
            <BrasserieTabell
              rows={weekRows.slice(0, 16)}
              labelFn={r => fmtVecka(r.label)}
              title="Veckoöversikt — senaste 90 dagar"
              fjolOf={fjolOfVecka}
            />
          )}

          {periodeFilter === 'manad' && (
            <BrasserieTabell
              rows={visaAllaManader ? monthlyRows : monthlyRows.slice(0, 12)}
              labelFn={r => fmtMonth(r.arManad)}
              title="Månadsintäkt — Brasserie + Vävstugan"
              fjolOf={fjolOfManad}
              footer={<VisaFlerRad total={monthlyRows.length} initial={12} expanded={visaAllaManader}
                onToggle={() => setVisaAllaManader(v => !v)} />}
            />
          )}

          {periodeFilter === 'period' && (
            <>
              <div className="card" style={{ paddingTop: 12, paddingBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Period</span>
                  <input type="date" value={pFrom} max={pTo} onChange={e => setPFrom(e.target.value)}
                    style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '4px 9px', fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>–</span>
                  <input type="date" value={pTo} min={pFrom} onChange={e => setPTo(e.target.value)}
                    style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '4px 9px', fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)' }} />
                  {[['30 dgr', -29], ['90 dgr', -89], ['12 mån', -364]].map(([lbl, d]) => (
                    <button key={lbl} onClick={() => { const t = new Date().toLocaleDateString('sv-SE'); setPTo(t); setPFrom(shiftISO(t, d)); }}
                      style={{ padding: '4px 12px', borderRadius: 20, border: '1px solid var(--border)', background: 'transparent',
                        color: 'var(--text-secondary)', fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>{lbl}</button>
                  ))}
                </div>
              </div>
              {(!brassSplit || !units) ? (
                <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar periodunderlag…</div></div>
              ) : (
                <BrasserieTabell
                  rows={[periodTot, ...periodRader]}
                  labelFn={periodLabel}
                  title={`Fri period — ${pFrom} → ${pTo}`}
                  fjolOf={fjolOfPeriod}
                  boldFirst
                />
              )}
            </>
          )}

          <div className="grid-2">
            <div className="card">
              <div className="card-title">Skydda — bärande veckor</div>
              <KpiRow label="V28–31 (sommar)"   value="" badge={{ type: 'green', text: 'Skydda' }} />
              <KpiRow label="V52–01 (jul/nyår)" value="" badge={{ type: 'green', text: 'Skydda' }} />
              <KpiRow label="V06–16 (vinter)"   value="" badge={{ type: 'green', text: 'Skydda' }} />
              <KpiRow label="Kväll à la carte"  value={ytdTB1Kvall > 0 ? `${tkr(ytdTB1Kvall)} TB1` : ''} badge={{ type: 'green', text: 'Motor' }} />
            </div>
            <div className="card">
              <div className="card-title">Stäng — spökveckor dag</div>
              <KpiRow label="V40–43 (höst)"  value="" badge={{ type: 'red',   text: 'Stäng dag' }} />
              <KpiRow label="V17–18 (vår)"   value="" badge={{ type: 'red',   text: 'Stäng dag' }} />
              <KpiRow label="V20–22 (vår)"   value="" badge={{ type: 'amber', text: 'Stäng dag' }} />
              <KpiRow label="Besparing"       value={ytdTB1Dag < 0 ? `~${tkr(Math.abs(ytdTB1Dag))} TB1` : '~116 tkr TB1'} badge={{ type: 'green', text: 'Effekt' }} />
            </div>
          </div>

          {/* YoY-utforskaren — gräv i utvecklingen mot ifjol */}
          <YoYUtforskare enheter={['Brasserie Kerstin', 'Vävstugan']} hoursEnhet="Brasserie Kerstin" color="#4A1B0C" />
        </>
      )}

      {/* ── FÖR PLANERING ── */}
      {sub === 'planering' && (
        <PlaneringTab enheter={['Brasserie Kerstin', 'Vävstugan']} color="#7A3B1C"
          splitConfig={{ loader: getBrasserieSplit, labels: ['Dag', 'Kväll'] }} />
      )}

      {/* ── ÖVRIGT NÖRDERI ── */}
      {sub === 'norderi' && (
        <>
          {/* Servicedagar (= Count DISTINCT Datum per kväll/dag) */}
          <div className="card">
            <div className="card-title">Servicedagar — dagar öppet för service, {RA_LBL}</div>
            <div className="grid-3">
              <NorderiKpi emoji="📅" label="Unika servicedagar" value={ytdKvittonTot > 0 ? ytdKvittonTot.toLocaleString('sv-SE') : '–'} sub="Dagar med Brasserie/Vävstugan-aktivitet" />
              <NorderiKpi emoji="🌙" label="Dagar med kvällsservice" value={ytdKvittonKvall > 0 ? ytdKvittonKvall.toLocaleString('sv-SE') : '–'} sub="À la carte-kvällar" color={KVALL_COLOR} />
              <NorderiKpi emoji="☀️" label="Dagar med dagservice" value={ytdKvittonDag > 0 ? ytdKvittonDag.toLocaleString('sv-SE') : '–'} sub="Lunch + café-dagar" color={DAG_COLOR} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginTop: 4 }}>
              Kväll + dag kan överlappa — restaurangen öppen för både lunch och middag samma dag
            </div>
          </div>

          {/* Artiklar sålda (= Linjenr Visbook = produktrader) */}
          <div className="card">
            <div className="card-title">Sälda artiklar (produktrader) — {RA_LBL}</div>
            <div className="grid-3">
              <NorderiKpi emoji="🍽️" label="Artiklar totalt" value={ytdPortTot > 0 ? Math.round(ytdPortTot).toLocaleString('sv-SE') : '–'} sub="Rätter + drycker alla pass" />
              <NorderiKpi emoji="🥂" label="Artiklar/kvällsdag" value={ytdPortKvall > 0 ? Math.round(ytdPortKvall).toLocaleString('sv-SE') : '–'} sub={sn(ytdPortKvall, ytdKvittonKvall) ? `${sn(ytdPortKvall, ytdKvittonKvall)} per kväll` : 'À la carte'} color={KVALL_COLOR} />
              <NorderiKpi emoji="🥗" label="Artiklar/dagservicedag" value={ytdPortDag > 0 ? Math.round(ytdPortDag).toLocaleString('sv-SE') : '–'} sub={sn(ytdPortDag, ytdKvittonDag) ? `${sn(ytdPortDag, ytdKvittonDag)} per dag` : 'Lunch + fika'} color={DAG_COLOR} />
            </div>
          </div>

          {/* Omsättning per servicedag */}
          <div className="card">
            <div className="card-title">Genomsnittlig omsättning per servicedag</div>
            <div className="grid-3">
              <NorderiKpi emoji="💰" label="Snitt oms/dag totalt" value={sn(ytdTot, ytdKvittonTot) ? sn(ytdTot, ytdKvittonTot).toLocaleString('sv-SE') + ' kr' : '–'} sub="Intäkt ÷ servicedagar" />
              <NorderiKpi emoji="🌙" label="Snitt oms/kvällsdag"  value={sn(ytdKvall, ytdKvittonKvall) ? sn(ytdKvall, ytdKvittonKvall).toLocaleString('sv-SE') + ' kr' : '–'} sub={snTkr(ytdTB1Kvall, ytdKvittonKvall) ? `TB1 ${snTkr(ytdTB1Kvall, ytdKvittonKvall)} tkr/kväll` : 'Middagsservice'} color={KVALL_COLOR} />
              <NorderiKpi emoji="☀️" label="Snitt oms/dagservicedag" value={sn(ytdDag, ytdKvittonDag) ? sn(ytdDag, ytdKvittonDag).toLocaleString('sv-SE') + ' kr' : '–'} sub={snTkr(ytdTB1Dag, ytdKvittonDag) ? `TB1 ${snTkr(ytdTB1Dag, ytdKvittonDag)} tkr/dag` : 'Café & lunch'} color={DAG_COLOR} />
            </div>
          </div>

          {/* Produktivitet */}
          <div className="card">
            <div className="card-title">Produktivitet — artiklar, intäkt/timme & personal%</div>
            <div className="grid-4">
              <NorderiKpi emoji="⚡" label="Artiklar/timme kväll" value={sn(ytdPortKvall, ytdTimBrassKvall) ? sn(ytdPortKvall, ytdTimBrassKvall) + ' st/h' : '–'} sub={sn(ytdKvall, ytdTimBrassKvall) ? `${sn(ytdKvall, ytdTimBrassKvall).toLocaleString('sv-SE')} kr/h` : null} color={KVALL_COLOR} />
              <NorderiKpi emoji="⚡" label="Artiklar/timme dag"   value={sn(ytdPortDag, ytdTimBrassDag) ? sn(ytdPortDag, ytdTimBrassDag) + ' st/h' : '–'}     sub={sn(ytdDag, ytdTimBrassDag) ? `${sn(ytdDag, ytdTimBrassDag).toLocaleString('sv-SE')} kr/h` : null} color={DAG_COLOR} />
              <NorderiKpi emoji="📊" label="Artiklar/servicedag"  value={sn(ytdPortTot, ytdKvittonTot) ? sn(ytdPortTot, ytdKvittonTot) + ' st' : '–'} sub="Produktrader per öppen dag" />
              <NorderiKpi emoji="👥" label="Personal%" value={ytdTot > 0 ? `${ytdPersBrass}%` : '–'} sub="Lönekostnad ÷ omsättning" color="#A32D2D" />
            </div>
          </div>

          {/* Bästa/sämsta månad */}
          <div className="grid-2">
            <div className="card">
              <div className="card-title">Bästa månad — oms/h</div>
              {bestMaand ? (
                <>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#3B6D11', fontFamily: 'system-ui,sans-serif' }}>{Math.round(bestMaand.brasserie / bestMaand.timBrass).toLocaleString('sv-SE')} kr/h</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginTop: 4 }}>{fmtMonth(bestMaand.arManad)} · {tkr(bestMaand.brasserie)} på {bestMaand.timBrass.toFixed(0)} h</div>
                </>
              ) : <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'system-ui,sans-serif' }}>Ingen data</div>}
            </div>
            <div className="card">
              <div className="card-title">Lägsta månad — oms/h</div>
              {worstMaand ? (
                <>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>{Math.round(worstMaand.brasserie / worstMaand.timBrass).toLocaleString('sv-SE')} kr/h</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginTop: 4 }}>{fmtMonth(worstMaand.arManad)} · {tkr(worstMaand.brasserie)} på {worstMaand.timBrass.toFixed(0)} h</div>
                </>
              ) : <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'system-ui,sans-serif' }}>Ingen data</div>}
            </div>
          </div>

          <div className="note" style={{ paddingLeft: 4 }}>
            Servicedagar = Count(DISTINCT Datum) · Artiklar = Linjenr Visbook · Källa Visbook · RÅ jul 2025 – jun 2026
          </div>
        </>
      )}
    </div>
  );
}

